import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  ChevronDown,
  Image as ImageIcon,
  LayoutDashboard,
  LogOut,
  MapPin,
  Menu,
  Ruler,
  Search,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  UserRound,
  UsersRound,
  WalletCards,
  X,
} from "lucide-react";
import { useCart } from "../../lib/cart";
import { useI18n } from "../../lib/i18n";
import { adfAssets } from "../storefront/assets";

export const ACCOUNT_NAV_GROUPS = [
  {
    key: "my",
    labelKey: "account.nav.group.my",
    items: [
      { key: "overview", labelKey: "account.tab.overview", icon: LayoutDashboard },
      { key: "profile", labelKey: "account.tab.profile", icon: UserRound },
      { key: "measurements", labelKey: "account.tab.measurements", icon: Ruler },
      { key: "addresses", labelKey: "account.tab.addresses", icon: MapPin },
    ],
  },
  {
    key: "shopping",
    labelKey: "account.nav.group.shopping",
    items: [
      { key: "orders", labelKey: "account.tab.orders", icon: ShoppingBag },
      { key: "wardrobe", labelKey: "account.tab.wardrobe", icon: Sparkles },
      { key: "tryon", labelKey: "account.tab.tryon", icon: ShieldCheck },
    ],
  },
  {
    key: "services",
    labelKey: "account.nav.group.services",
    items: [
      { key: "avatar", labelKey: "account.tab.avatar", icon: ImageIcon },
      { key: "appointments", labelKey: "account.tab.appointments", icon: CalendarDays },
      { key: "concierge", labelKey: "account.tab.concierge", icon: UsersRound },
    ],
  },
  {
    key: "falcon",
    labelKey: "account.nav.group.falcon",
    items: [
      { key: "loyalty", labelKey: "account.tab.loyalty", icon: Sparkles },
      { key: "benefits", labelKey: "account.tab.benefits", icon: WalletCards },
    ],
  },
  {
    key: "settings",
    labelKey: "account.nav.group.settings",
    items: [
      { key: "notifications", labelKey: "account.tab.notifications", icon: Bell },
      { key: "privacy", labelKey: "account.tab.privacy", icon: ShieldCheck },
    ],
  },
];

export function findAccountNavItem(tab) {
  return ACCOUNT_NAV_GROUPS.flatMap((group) => group.items).find((item) => item.key === tab) || ACCOUNT_NAV_GROUPS[0].items[0];
}

function initialsFor(user) {
  const first = user?.first_name?.trim()?.[0] || "";
  const last = user?.last_name?.trim()?.[0] || "";
  return `${first}${last}`.toUpperCase() || "ADF";
}

function AccountHeader({ user, onLogout }) {
  const navigate = useNavigate();
  const { itemCount } = useCart() || { itemCount: 0 };
  const { locale, setLocale, t } = useI18n();
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const submitSearch = (event) => {
    event.preventDefault();
    const term = query.trim();
    if (!term) return;
    setSearchOpen(false);
    navigate(`/shop?q=${encodeURIComponent(term)}`);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-graphite/10 bg-ivory/95 backdrop-blur" data-testid="account-header">
      <div className="mx-auto flex h-16 max-w-[1320px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-4">
          <Link to="/" className="flex min-w-0 items-center gap-2" aria-label="Atelier Di Falco">
            <img src={adfAssets.logoMono} alt="" className="h-8 w-auto" />
            <img src={adfAssets.logoWord} alt="Atelier Di Falco" className="hidden h-7 w-auto sm:block" />
          </Link>
          <Link to="/shop" className="hidden min-h-11 items-center text-sm text-graphite/75 transition hover:text-espresso md:inline-flex">
            {t("account.header.back_to_shop")}
          </Link>
        </div>

        <div className="flex items-center justify-end gap-1 sm:gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen((open) => !open)}
            className="inline-flex h-11 w-11 items-center justify-center text-graphite/75 transition hover:text-espresso focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso"
            aria-label={t("account.header.search")}
            aria-expanded={searchOpen}
          >
            {searchOpen ? <X size={18} strokeWidth={1.5} /> : <Search size={18} strokeWidth={1.5} />}
          </button>
          <button
            type="button"
            onClick={() => setLocale(locale === "az" ? "en" : "az")}
            className="inline-flex h-11 min-w-11 items-center justify-center text-sm text-graphite/70 transition hover:text-espresso focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso"
            aria-label={t("account.header.language")}
          >
            {locale.toUpperCase()}
          </button>
          <NavLink
            to="/cart"
            className="relative inline-flex h-11 w-11 items-center justify-center text-graphite/75 transition hover:text-espresso focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso"
            aria-label={t("account.header.cart")}
          >
            <ShoppingBag size={18} strokeWidth={1.5} />
            {itemCount > 0 && (
              <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center bg-graphite px-1 text-[10px] leading-none text-ivory">
                {itemCount > 9 ? "9+" : itemCount}
              </span>
            )}
          </NavLink>
          <div className="relative">
            <button
              type="button"
              onClick={() => setProfileOpen((open) => !open)}
              className="inline-flex h-11 items-center gap-2 border border-graphite/15 px-2.5 text-sm text-graphite transition hover:border-espresso focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso"
              aria-label={t("account.header.profile_menu")}
              aria-expanded={profileOpen}
            >
              <span className="flex h-7 w-7 items-center justify-center bg-graphite text-[11px] text-ivory">
                {initialsFor(user)}
              </span>
              <ChevronDown size={14} strokeWidth={1.5} className="hidden sm:block" />
            </button>
            {profileOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 border border-graphite/12 bg-ivory p-2 shadow-sm">
                <Link
                  to="/account?tab=profile"
                  onClick={() => setProfileOpen(false)}
                  className="block min-h-11 px-3 py-2 text-sm text-graphite/75 transition hover:bg-stone/30 hover:text-graphite"
                >
                  {t("account.tab.profile")}
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setProfileOpen(false);
                    onLogout();
                  }}
                  className="flex min-h-11 w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-900 transition hover:bg-red-50"
                >
                  <LogOut size={15} strokeWidth={1.5} /> {t("account.nav.logout")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {searchOpen && (
        <form onSubmit={submitSearch} className="border-t border-graphite/10 bg-paper" role="search">
          <div className="mx-auto flex max-w-[1320px] items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
            <Search size={18} strokeWidth={1.5} className="text-graphite/70" />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="min-h-11 min-w-0 flex-1 bg-transparent text-sm text-graphite outline-none placeholder:text-graphite/70"
              placeholder={t("account.header.search_placeholder")}
              aria-label={t("account.header.search")}
            />
            <button type="submit" className="min-h-11 bg-graphite px-5 text-sm text-ivory transition hover:bg-espresso">
              {t("account.header.search_submit")}
            </button>
          </div>
        </form>
      )}
    </header>
  );
}

function AccountNav({ activeTab, onNavigate, onLogout, mobile = false }) {
  const { t } = useI18n();
  return (
    <nav aria-label={t("account.nav.aria")} data-testid="account-tabs" className={mobile ? "space-y-6" : "space-y-7"}>
      {ACCOUNT_NAV_GROUPS.map((group) => (
        <div key={group.key}>
          <div className="mb-2 px-3 text-xs font-medium text-graphite/70">{t(group.labelKey)}</div>
          <div className="space-y-1">
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = activeTab === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  data-testid={`tab-${item.key}`}
                  onClick={() => onNavigate(item.key)}
                  className={`flex min-h-11 w-full items-center gap-3 border-l-2 px-3 py-2 text-left text-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso ${
                    active
                      ? "border-espresso bg-stone/30 text-graphite"
                      : "border-transparent text-graphite/70 hover:bg-paper hover:text-graphite"
                  }`}
                  aria-current={active ? "page" : undefined}
                >
                  <Icon size={16} strokeWidth={1.5} className={active ? "text-espresso" : "text-graphite/70"} />
                  <span>{t(item.labelKey)}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="border-t border-graphite/10 pt-4">
        <button
          type="button"
          data-testid="account-logout"
          onClick={onLogout}
          className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left text-sm text-red-900 transition hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-900/40"
        >
          <LogOut size={16} strokeWidth={1.5} /> {t("account.nav.logout")}
        </button>
      </div>
    </nav>
  );
}

function MobileAccountNav({ activeTab, onNavigate, onLogout }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const active = useMemo(() => findAccountNavItem(activeTab), [activeTab]);

  const go = (key) => {
    onNavigate(key);
    setOpen(false);
  };

  return (
    <div className="lg:hidden">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-6 flex min-h-12 w-full items-center justify-between border border-graphite/15 bg-paper px-4 text-left text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso"
        aria-expanded={open}
      >
        <span>{t("account.mobile.current_section")}: {t(active.labelKey)}</span>
        <Menu size={18} strokeWidth={1.5} />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-graphite/30" role="dialog" aria-modal="true">
          <div className="ml-auto flex h-full w-[min(88vw,380px)] flex-col bg-ivory">
            <div className="flex min-h-16 items-center justify-between border-b border-graphite/10 px-5">
              <span className="font-serif text-2xl text-graphite">{t("account.nav.title")}</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso"
                aria-label={t("common.close")}
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <AccountNav activeTab={activeTab} onNavigate={go} onLogout={onLogout} mobile />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AccountFooter() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-graphite/10 bg-paper" data-testid="account-footer">
      <div className="mx-auto flex max-w-[1320px] flex-col gap-3 px-4 py-6 text-sm text-graphite/70 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
        <div>© {new Date().getFullYear()} Atelier Di Falco</div>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          <Link to="/privacy" className="transition hover:text-espresso">{t("account.footer.privacy")}</Link>
          <Link to="/terms" className="transition hover:text-espresso">{t("account.footer.terms")}</Link>
          <Link to="/contact" className="transition hover:text-espresso">{t("account.footer.contact")}</Link>
          <Link to="/shop" className="transition hover:text-espresso">{t("account.footer.shop")}</Link>
        </div>
      </div>
    </footer>
  );
}

function IdentityCard({ user, tier }) {
  const { t } = useI18n();
  return (
    <div className="border border-graphite/10 bg-graphite p-5 text-ivory">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center border border-ivory/25 font-serif text-lg">
          {initialsFor(user)}
        </span>
        <span className="min-w-0">
          <span className="block truncate font-serif text-xl leading-tight">
            {[user?.first_name, user?.last_name].filter(Boolean).join(" ") || t("account.common.client")}
          </span>
          <span className="mt-0.5 block truncate text-xs text-ivory/58">{user?.email || user?.phone || ""}</span>
        </span>
      </div>
      {tier && (
        <div className="mt-4 border-t border-ivory/15 pt-3">
          <span className="adf-small text-ivory/58">{t("account.overview.hero.tier")}</span>
          <span className="mt-1 block font-serif text-lg text-gilt">{tier}</span>
        </div>
      )}
    </div>
  );
}

export default function AccountLayout({ user, activeTab, onNavigate, onLogout, tier = null, children }) {
  return (
    <div className="flex min-h-screen flex-col bg-ivory text-graphite" data-testid="account-page">
      <AccountHeader user={user} onLogout={onLogout} />
      <main id="main" className="flex-1">
        <div className="mx-auto grid max-w-[1320px] grid-cols-1 gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[244px_minmax(0,1fr)] lg:gap-12 lg:px-8 lg:py-10 xl:gap-14">
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-7">
              <IdentityCard user={user} tier={tier} />
              <AccountNav activeTab={activeTab} onNavigate={onNavigate} onLogout={onLogout} />
            </div>
          </aside>
          <section className="min-w-0">
            <MobileAccountNav activeTab={activeTab} onNavigate={onNavigate} onLogout={onLogout} />
            {children}
          </section>
        </div>
      </main>
      <AccountFooter />
    </div>
  );
}
